import { useEffect } from "react";
import { AppStoreButton, ArrowIcon } from "../components/Shared";

const APP_STORE_URL = "https://apps.apple.com/app/id6636477989";
const PRIVACY_URL = "https://taspace.top/legal/scanbyte/privacy/latest";
const TERMS_URL = "https://taspace.top/legal/scanbyte/terms/latest";
const SUPPORT_EMAIL = "fingertipstudio@outlook.com";

const features = [
  {
    number: "01",
    title: "Scan QR codes & barcodes",
    body: "Point your camera at a QR code, UPC, EAN or ISBN. ScanByte recognizes the code automatically, so there is no scanner mode to choose.",
    image: "/assets/scanbyte/scan.webp",
    alt: "ScanByte scanning a QR code",
  },
  {
    number: "02",
    title: "Identify products & books",
    body: "See available product and book details such as an image, name, brand, author and source. If a lookup is unavailable, you can still search, copy or share.",
    image: "/assets/scanbyte/product.webp",
    alt: "ScanByte product result",
    dark: true,
  },
  {
    number: "03",
    title: "Keep useful scan history",
    body: "Find scanned products, books and links again later. Search by product name, brand, book title, author, domain or barcode, and favorite the results you want to keep close.",
    image: "/assets/scanbyte/history.webp",
    alt: "ScanByte scan history",
  },
  {
    number: "04",
    title: "Create QR codes & barcodes",
    body: "Create codes for text, websites, Wi-Fi, phone numbers, messages, email and contacts. Generate EAN-13, UPC-A, Code 128 and Code 39 barcodes, then save or share them.",
    image: "/assets/scanbyte/create.webp",
    alt: "ScanByte code generator",
  },
];

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setFavicon(href: string) {
  let icon = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  icon.type = "image/webp";
  icon.href = href;
}

function ScanBytePage() {
  useEffect(() => {
    document.title = "ScanByte — Barcode & QR Scanner";
    setFavicon("/assets/scanbyte/app-icon.webp");
    upsertMeta("name", "description", "Scan QR codes, product barcodes and ISBNs. Identify products and books, keep useful scan history, and create your own codes—free, with no ads or login.");
    upsertMeta("property", "og:title", "ScanByte — Barcode & QR Scanner");
    upsertMeta("property", "og:description", "Scan, understand and keep the codes you use every day.");
    upsertMeta("property", "og:url", "https://app.taspace.top/scanbyte");
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://app.taspace.top/scanbyte";
  }, []);

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand brand--scanbyte" href="#top" aria-label="ScanByte home">
          <img src="/assets/scanbyte/app-icon.webp" alt="" width="256" height="256" />
          <span>ScanByte</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#features">Features</a>
          <a href="#privacy">Privacy</a>
          <a className="nav-download" href={APP_STORE_URL} target="_blank" rel="noreferrer">Get the app <ArrowIcon /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero section-wrap">
          <div className="hero-copy">
            <p className="eyebrow">Barcode &amp; QR scanner</p>
            <h1>Scan any code.<br /><span>Keep what matters.</span></h1>
            <p className="hero-lede">Scan QR codes, product barcodes and ISBNs. Identify products and books, keep useful history, and create your own codes—free, with no ads or login.</p>
            <div className="hero-actions">
              <AppStoreButton href={APP_STORE_URL} />
              <a className="text-link" href="#features">See what it can do <span aria-hidden="true">↓</span></a>
            </div>
            <ul className="trust-list" aria-label="Product benefits">
              <li>Free</li>
              <li>No ads</li>
              <li>No login</li>
            </ul>
          </div>

          <div className="hero-visual" aria-label="ScanByte app screenshot">
            <div className="phone-frame">
              <img src="/assets/scanbyte/scan.webp" alt="ScanByte scanning screen" width="600" height="1304" fetchPriority="high" />
            </div>
            <div className="privacy-float"><span aria-hidden="true">✦</span> History stays on your device</div>
          </div>
        </section>

        <section className="intro section-wrap" id="features">
          <p className="eyebrow">One scanner, four useful things</p>
          <div className="intro-heading">
            <h2>From a scan<br />to something useful.</h2>
            <p>No scanner modes, no accounts, no ads. Point at a code, act on the result, and find it again later.</p>
          </div>
        </section>

        <section className="feature-grid section-wrap" aria-label="ScanByte features">
          {features.map((feature) => (
            <article key={feature.number} className={`scanbyte-feature-card${feature.dark ? " scanbyte-feature-card--dark" : ""}`}>
              <div className="scanbyte-feature-copy"><span>{feature.number}</span><h3>{feature.title}</h3><p>{feature.body}</p></div>
              <div className="scanbyte-feature-shot">
                <img src={feature.image} alt={feature.alt} width="600" height="1304" loading="lazy" />
              </div>
            </article>
          ))}
        </section>

        <section className="privacy-section section-wrap" id="privacy">
          <div className="privacy-symbol" aria-hidden="true">⌁</div>
          <div className="privacy-copy">
            <p className="eyebrow">Private by design</p>
            <h2>Your scans stay<br />with you.</h2>
            <p>Scan history and favorites are stored on your device. ScanByte does not include raw scan content in analytics, and it sends a barcode or ISBN only when you use product or book lookup—or when you explicitly choose to attach one to feedback.</p>
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Read our Privacy Policy <ArrowIcon /></a>
          </div>
          <div className="privacy-points">
            <div><strong>01</strong><span>History stored on device</span></div>
            <div><strong>02</strong><span>No raw scan content in analytics</span></div>
            <div><strong>03</strong><span>Lookup only when you choose</span></div>
          </div>
        </section>

        <section className="closing closing--scanbyte section-wrap">
          <img src="/assets/scanbyte/app-icon.webp" alt="ScanByte app icon" width="256" height="256" loading="lazy" />
          <p className="eyebrow">Available for iPhone and iPad</p>
          <h2>The code you need<br />is one scan away.</h2>
          <AppStoreButton href={APP_STORE_URL} inverse />
        </section>
      </main>

      <footer className="site-footer section-wrap">
        <div className="footer-brand"><img src="/assets/scanbyte/app-icon.webp" alt="" width="256" height="256" loading="lazy" /><div><strong>ScanByte</strong><span>Scan, identify and create codes.</span></div></div>
        <div className="footer-links" aria-label="Legal and support">
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
          <a href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
        </div>
        <p>© 2026 Fingertip Studio. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default ScanBytePage;
